import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  Announcements,
  ClientRect,
  Collision,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  Modifier,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import { useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { SkillTreeItemIcon } from './skill-file-icon';
import { formatSkillTreeItemId, parseSkillTreeItemId } from './skill-tree-ids';

/**
 * Moving files and folders of a skill by dragging them in its file tree.
 *
 * A drag only moves an item into another folder. The tree keeps its own order (see
 * `buildSkillFileTree`), so nothing shifts while dragging: the row stays put, a copy of it
 * follows the pointer, and the folder it would land in is highlighted. Dropping on a file
 * moves into that file's folder, and on empty space below the rows moves to the top level.
 * A collapsed folder opens when the pointer rests on it.
 */

/** What the rows show during a drag. */
export interface SkillTreeRowsDragState {
  /**
   * The dragged row and, for a folder, every row in it. The tree view renders its rows as a
   * flat list, so a folder's rows are marked one by one.
   */
  draggedIds: ReadonlySet<string>;
  /**
   * The row of the folder a drop would land in. Only that row is marked: marking the files in
   * it too made them look selected.
   */
  dropTargetId: string | null;
}

interface SkillTreeDragState {
  /** Tree id of the row being dragged. */
  activeId: string | null;
  /** The folder a drop would move the row into, '' for the top level, or null for nowhere. */
  dropFolder: string | null;
}

const NO_ROWS: ReadonlySet<string> = new Set();
const IDLE_DRAG_STATE: SkillTreeDragState = { activeId: null, dropFolder: null };
const SkillTreeRowsDragContext = createContext<SkillTreeRowsDragState>({
  draggedIds: NO_ROWS,
  dropTargetId: null,
});

/** The outline of the folder, or the area, a drop would land in. */
export const DROP_TARGET_COLOR = '#1677FF';
/** How long the pointer rests on a collapsed folder before it opens. */
const EXPAND_DELAY_MS = 500;
const ROOT_DROP_ID = 'skill-tree-root';
const MEASURING = {
  droppable: { frequency: 100, strategy: MeasuringStrategy.WhileDragging },
};
/** Where the dragged copy sits from the pointer: just right of it, centered on it. */
const OVERLAY_OFFSET = { x: 12, y: -16 };
const OVERLAY_MODIFIERS: Modifier[] = [followPointer];

const useStyles = makeStyles()(theme => ({
  overlay: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 6,
    boxShadow: theme.shadows[4],
    cursor: 'grabbing',
    display: 'inline-flex',
    fontSize: 14,
    gap: 8,
    maxWidth: 240,
    padding: '6px 10px',
  },
  overlayName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  root: {
    borderRadius: 6,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflowY: 'auto',
    // Leaves room to drop at the top level even when the rows fill the panel.
    paddingBottom: 24,
  },
  // Rows ignore the pointer while dragging, so none shows its hover actions.
  rootDragging: { '& > *': { pointerEvents: 'none' }, cursor: 'grabbing' },
  // An outline rather than a fill, which would read as every row being selected.
  rootTarget: { boxShadow: `inset 0 0 0 1.5px ${DROP_TARGET_COLOR}` },
}));

/** The area around the tree, where a drop outside every row goes to the top level. */
const SkillTreeDropRoot = React.memo(function SkillTreeDropRoot({
  children,
  dragging,
  highlighted,
}: {
  children: React.ReactNode;
  dragging: boolean;
  highlighted: boolean;
}) {
  const { classes, cx } = useStyles();
  const { setNodeRef } = useDroppable({ id: ROOT_DROP_ID });
  return (
    <div
      ref={setNodeRef}
      className={cx(
        classes.root,
        dragging && classes.rootDragging,
        highlighted && classes.rootTarget
      )}
    >
      {children}
    </div>
  );
});

/** The copy of the dragged row that follows the pointer. */
const DraggedRowPreview = React.memo(function DraggedRowPreview({
  contentType,
  isFolder,
  name,
}: {
  contentType: string;
  isFolder: boolean;
  name: string;
}) {
  const { classes } = useStyles();
  return (
    <div className={classes.overlay}>
      <SkillTreeItemIcon contentType={contentType} isFolder={isFolder} size={18} />
      <span className={classes.overlayName}>{name}</span>
    </div>
  );
});

/**
 * Lets the rows of the tree inside it be dragged into folders. `children` is the tree view;
 * the empty space under it is the top level.
 */
export const SkillTreeDragArea = React.memo(function SkillTreeDragArea({
  canMove,
  children,
  expandFolder,
  getContentType,
  getDropFolder,
  getIsFolderExpanded,
  getLabel,
  getSubtreeIds,
  move,
}: {
  /** Whether the row can move into the folder. */
  canMove: (itemId: string, folder: string) => boolean;
  children: React.ReactNode;
  expandFolder: (path: string) => void;
  /** What the row's file is, for its icon; '' for a folder. */
  getContentType: (itemId: string) => string;
  /** The folder a drop on the row lands in: a folder itself, or a file's folder. */
  getDropFolder: (itemId: string) => string | null;
  getIsFolderExpanded: (path: string) => boolean;
  getLabel: (itemId: string) => string;
  /** The row and every row under it. */
  getSubtreeIds: (itemId: string) => string[];
  move: (itemId: string, folder: string) => void;
}) {
  const theme = useTheme();
  const sensors = useSensors(
    useSensor(SkillTreePointerSensor, { activationConstraint: { distance: 4 } })
  );
  const [state, setState] = useState(IDLE_DRAG_STATE);
  const expandTimerRef = useRef<number | null>(null);

  const clearExpandTimer = useCallback(() => {
    if (expandTimerRef.current !== null) window.clearTimeout(expandTimerRef.current);
    expandTimerRef.current = null;
  }, []);
  /** The folder a drop of `activeId` on `overId` moves it into, or null when it cannot. */
  const getValidTargetFolder = useCallback(
    (activeId: UniqueIdentifier, overId: UniqueIdentifier | undefined) => {
      if (typeof activeId !== 'string') return null;
      if (overId === ROOT_DROP_ID) return canMove(activeId, '') ? '' : null;
      const folder = typeof overId === 'string' ? getDropFolder(overId) : null;
      return folder !== null && canMove(activeId, folder) ? folder : null;
    },
    [canMove, getDropFolder]
  );

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    if (typeof active.id === 'string') setState({ activeId: active.id, dropFolder: null });
  }, []);
  const handleDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      clearExpandTimer();
      const dropFolder = getValidTargetFolder(active.id, over?.id);
      setState(current =>
        current.dropFolder === dropFolder ? current : { ...current, dropFolder }
      );
      const overItem = typeof over?.id === 'string' ? parseSkillTreeItemId(over.id) : null;
      if (overItem?.kind !== 'folder' || getIsFolderExpanded(overItem.path)) return;
      expandTimerRef.current = window.setTimeout(
        () => expandFolder(overItem.path),
        EXPAND_DELAY_MS
      );
    },
    [clearExpandTimer, expandFolder, getIsFolderExpanded, getValidTargetFolder]
  );
  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      clearExpandTimer();
      setState(IDLE_DRAG_STATE);
      const folder = getValidTargetFolder(active.id, over?.id);
      if (folder !== null && typeof active.id === 'string') move(active.id, folder);
    },
    [clearExpandTimer, getValidTargetFolder, move]
  );
  const handleDragCancel = useCallback(() => {
    clearExpandTimer();
    setState(IDLE_DRAG_STATE);
  }, [clearExpandTimer]);

  // The default announcements read the tree's internal ids aloud.
  const accessibility = useMemo(() => {
    const getName = (id: UniqueIdentifier) => (typeof id === 'string' ? getLabel(id) : '');
    const getFolderName = (folder: string | null) =>
      folder === null ? null : folder || 'the top level';
    const announcements: Announcements = {
      onDragCancel: ({ active }) => `Stopped moving ${getName(active.id)}.`,
      onDragEnd: ({ active, over }) => {
        const folder = getFolderName(getValidTargetFolder(active.id, over?.id));
        return folder
          ? `Moved ${getName(active.id)} to ${folder}.`
          : `${getName(active.id)} was not moved.`;
      },
      onDragOver: ({ active, over }) => {
        const folder = getFolderName(getValidTargetFolder(active.id, over?.id));
        return folder ? `Over ${folder}.` : undefined;
      },
      onDragStart: ({ active }) => `Picked up ${getName(active.id)}.`,
    };
    // Dragging takes a pointer; the Move button on each row does the same from the keyboard.
    return {
      announcements,
      screenReaderInstructions: { draggable: 'To move an item, use its Move button.' },
    };
  }, [getLabel, getValidTargetFolder]);

  const rowsState = useMemo<SkillTreeRowsDragState>(
    () => ({
      draggedIds: state.activeId ? new Set(getSubtreeIds(state.activeId)) : NO_ROWS,
      dropTargetId: state.dropFolder
        ? formatSkillTreeItemId({ kind: 'folder', path: state.dropFolder })
        : null,
    }),
    [getSubtreeIds, state]
  );
  const activeItem = state.activeId ? parseSkillTreeItemId(state.activeId) : null;
  const activeName = state.activeId ? getLabel(state.activeId) : '';
  const activeContentType = state.activeId ? getContentType(state.activeId) : '';
  return (
    <DndContext
      accessibility={accessibility}
      collisionDetection={detectInnermostDropTarget}
      measuring={MEASURING}
      sensors={sensors}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
    >
      <SkillTreeRowsDragContext.Provider value={rowsState}>
        <SkillTreeDropRoot dragging={state.activeId !== null} highlighted={state.dropFolder === ''}>
          {children}
        </SkillTreeDropRoot>
      </SkillTreeRowsDragContext.Provider>
      <DragOverlay dropAnimation={null} modifiers={OVERLAY_MODIFIERS} zIndex={theme.zIndex.tooltip}>
        {activeItem && (
          <DraggedRowPreview
            contentType={activeContentType}
            isFolder={activeItem.kind === 'folder'}
            name={activeName}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
});

export function useSkillTreeRowsDrag(): SkillTreeRowsDragState {
  return useContext(SkillTreeRowsDragContext);
}

/**
 * A pointer sensor that leaves buttons and inputs alone, and ignores events that only bubble
 * through a row from a menu or dialog it opened in a portal.
 */
class SkillTreePointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: ({ currentTarget, nativeEvent: event, target }: React.PointerEvent) => {
        if (!event.isPrimary || event.button !== 0) return false;
        if (!(target instanceof Element) || !currentTarget.contains(target)) return false;
        return !target.closest('button, input, textarea');
      },
    },
  ];
}

/**
 * The dragged copy starts where the row's list item does, which is far left of the pointer
 * for a nested row, so it is moved next to the pointer instead.
 */
function followPointer({
  activatorEvent,
  draggingNodeRect,
  transform,
}: Parameters<Modifier>[0]): ReturnType<Modifier> {
  const pointer = activatorEvent && getEventCoordinates(activatorEvent);
  if (!pointer || !draggingNodeRect) return transform;
  return {
    ...transform,
    x: transform.x + pointer.x - draggingNodeRect.left + OVERLAY_OFFSET.x,
    y: transform.y + pointer.y - draggingNodeRect.top + OVERLAY_OFFSET.y,
  };
}

/**
 * The pointer is over a row and over the area around the tree at once. The smallest drop
 * area wins, so the row does, and the area only counts where no row is.
 */
function detectInnermostDropTarget(args: Parameters<CollisionDetection>[0]): Collision[] {
  return pointerWithin(args).sort(
    (left, right) =>
      getRectArea(args.droppableRects.get(left.id)) - getRectArea(args.droppableRects.get(right.id))
  );
}

function getRectArea(rect: ClientRect | undefined): number {
  return rect ? rect.width * rect.height : Infinity;
}
